import { useState } from "react";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";
import { fullPageLoad } from "../navigate";
import { useSession } from "../session";

const t = copy.websites.pricing;

// Where a visitor goes to sign up before choosing a plan, and back after.
export const SIGNUP_FOR_PRICING = `/register?next=${encodeURIComponent("/websites-for-producers#pricing")}`;

// The tiers the live /websites page sells. Checkout prices them on the
// server by plan name; the browser never sends an amount.
async function startCheckout(plan: string): Promise<string | null> {
  try {
    const res = await fetch("/v1/subscription/checkout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ tier_name: plan }),
    });
    const data = (await res.json().catch(() => null)) as { success?: unknown; payment_url?: unknown } | null;
    if (res.ok && data?.success === true && typeof data.payment_url === "string" && /^https:\/\//.test(data.payment_url)) {
      return data.payment_url;
    }
    return null;
  } catch {
    return null;
  }
}

export default function Pricing() {
  const session = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const choose = async (plan: string) => {
    setError("");
    if (session.kind === "visitor") {
      fullPageLoad(SIGNUP_FOR_PRICING);
      return;
    }
    setBusy(plan);
    const url = await startCheckout(plan);
    if (url) {
      fullPageLoad(url);
      return;
    }
    setError(t.failed);
    setBusy(null);
  };

  return (
    <section id="pricing" data-testid="pricing" className="border-t border-ground-edge bg-ground-raised px-4 py-20 md:px-6 md:py-28">
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{t.eyebrow}</p>
          <h2 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl">{t.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.sub}</p>
        </div>
        {error && (
          <p role="alert" className="mt-8 rounded-md border border-alert/40 bg-alert/10 px-4 py-3 text-sm text-chrome">
            {error}
          </p>
        )}
        <ul className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {t.tiers.map((tier) => (
            <li
              key={tier.name}
              data-testid="tier"
              className={`relative flex flex-col rounded-2xl border p-6 md:p-8 ${
                tier.highlight ? "border-money/50 bg-money/5" : "border-ground-edge bg-ground"
              }`}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-6 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ground">
                  {t.recommended}
                </span>
              )}
              <div className="pt-2">
                <h3 className="font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{tier.name}</h3>
                <p className="mt-1 text-sm text-chrome-dim">{tier.description}</p>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="bf-tabular font-headline text-5xl leading-none text-chrome">{tier.price}</span>
                <span className="text-sm text-chrome-dim">{t.perMonth}</span>
              </div>
              <div className={`mt-6 rounded-lg p-4 ${tier.highlight ? "bg-money/10" : "bg-ground-raised"}`}>
                <p className={`text-sm font-medium ${tier.highlight ? "text-money" : "text-chrome"}`}>{tier.outcome}</p>
              </div>
              <button
                type="button"
                onClick={() => choose(tier.plan)}
                disabled={busy !== null}
                className={`bf-focusable mt-6 flex h-12 w-full items-center justify-center whitespace-nowrap rounded-md text-xs font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-60 ${
                  tier.highlight
                    ? "bg-money text-ground hover:bg-money-deep"
                    : "border border-ground-edge text-chrome hover:border-gold/50 hover:text-gold"
                }`}
              >
                {busy === tier.plan ? t.redirecting : tier.cta}
              </button>
              <ul className="mt-8 space-y-3">
                {tier.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-3">
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs ${
                        tier.highlight ? "bg-money/15 text-money" : "bg-ground-raised text-gold"
                      }`}
                    >
                      <Icon name={f.icon as IconName} />
                    </span>
                    <span className={`text-sm ${"bold" in f && f.bold ? "font-semibold text-chrome" : "text-chrome-dim"}`}>{f.text}</span>
                  </li>
                ))}
              </ul>
              {tier.limitations.length > 0 && (
                <ul className="mt-6 space-y-3 border-t border-ground-edge pt-6">
                  {tier.limitations.map((l) => (
                    <li key={l} className="flex items-start gap-3">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-ground-raised text-xs text-chrome-dim">
                        <Icon name="closeCircle" />
                      </span>
                      <span className="text-sm text-chrome-dim">{l}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-12 text-sm text-chrome">{t.footnote}</p>
      </div>
    </section>
  );
}
